import React, { useEffect, useState } from "react";

// Shown while an application is being tailored. That wait is MEASURED at ~41 seconds — the résumé
// rewrite alone is ~40s of it — so this replaces a lone spinner that sat still for most of a minute.
//
// It is deliberately AMBIENT: a résumé-shaped outline whose lines shimmer and periodically rewrite
// themselves. It never ticks a section off or claims a percentage, because it genuinely cannot know:
// the browser makes ONE request and gets everything back at the end, so any "Summary ✓ done" would be
// a guess dressed as a fact. What it does show is true — a document is being rewritten, right now.

const SECTIONS = [
  { label: "Summary",    rows: [96, 88, 72] },
  { label: "Experience", rows: [92, 84, 90, 68] },
  { label: "Skills",     rows: [78, 86] },
];
const ROWS = SECTIONS.reduce((n, s) => n + s.rows.length, 0);
const SWAP_MS = 1400;          // how often a line reworks itself
const SETTLE_MS = 420;         // how long it stays highlighted mid-rewrite

export default function TailoringSkeleton() {
  // `active` is the row currently being rewritten; `widths` holds each row's current length, so a
  // rewritten line visibly comes back a different size rather than just flashing.
  const [active, setActive] = useState(-1);
  const [widths, setWidths] = useState(() => SECTIONS.flatMap((s) => s.rows));

  useEffect(() => {
    let settle;
    const tick = setInterval(() => {
      const i = Math.floor(Math.random() * ROWS);
      setActive(i);
      settle = setTimeout(() => {
        setWidths((w) => w.map((v, j) => (j === i ? 62 + Math.round(Math.random() * 36) : v)));
        setActive(-1);
      }, SETTLE_MS);
    }, SWAP_MS);
    return () => { clearInterval(tick); clearTimeout(settle); };
  }, []);

  let row = -1;
  return (
    <div className="py-8 px-2">
      <div className="mx-auto max-w-sm rounded-2xl border border-border bg-white shadow-sm p-5 overflow-hidden">
        {/* Letterhead */}
        <div className="flex items-center gap-3 pb-4 mb-4 border-b border-border">
          <div className="w-10 h-10 rounded-xl bg-[#0025D8]/10 vh-shimmer flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-3 w-32 rounded bg-[#0F172A]/[0.09] vh-shimmer" />
            <div className="h-2 w-24 rounded bg-[#0F172A]/[0.06] vh-shimmer mt-2" />
          </div>
        </div>

        {SECTIONS.map((s) => (
          <div key={s.label} className="mb-4 last:mb-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#0F172A]/35 mb-2">
              {s.label}
            </div>
            <div className="space-y-1.5">
              {s.rows.map(() => {
                row += 1;
                const i = row;
                const on = active === i;
                return (
                  <div key={i} className="h-2 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${widths[i]}%`,
                      background: on ? "rgba(0,37,216,0.35)" : "rgba(15,23,42,0.08)",
                    }} />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="text-center font-semibold text-[#0F172A] mt-6">Tailoring your application…</p>
      {/* Honest about the wait. This used to say "a few seconds" for a ~41-second operation. */}
      <p className="text-center text-sm text-muted-foreground mt-1">
        Reading the job, rewriting your résumé to match it, and drafting your answers.
        <br className="hidden sm:block" /> This usually takes under a minute.
      </p>
    </div>
  );
}
